import { type FormEvent, useEffect, useId, useState } from 'react';

import {
  type Content,
  createPost,
  deletePost,
  fetchPosts,
  markPostPosted,
  type Post,
  type SharePost,
  undoPostPosted,
  updatePost,
  type Version,
  type VersionFile,
} from '../api.ts';
import { useAssetUrls } from '../assets.ts';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import { DASHBOARD_POLL_MS, describeError, useNow } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { useToast } from '../toast.tsx';
import { Badge } from '../ui.tsx';
import * as wording from '../wording.ts';
import { postMoment, postPlatformLabel } from './lines.ts';
import {
  draftLabelMissing,
  draftOfPost,
  newPostDraft,
  type PostDraft,
  PostFields,
  postInputOf,
} from './PostForm.tsx';
import { composerFor, defaultPostFormat, shareCopyPlatforms } from './posting.ts';
import { useVersions } from './VersionSection.tsx';

/** What the panel needs of the content besides its posts. */
export type PostsPanelContent = Pick<Content, 'name' | 'status' | 'formats' | 'shareCopy' | 'currentVersion'>;

/**
 * A content's posts (US-024): one row per post, with what it takes to get a
 * planned one out — its caption on the clipboard, the right file, the
 * platform's composer — and to record it once it is out. Posts are planned at
 * any status; marking one posted waits for a delivered content.
 *
 * The board drawer passes the card's `posts` and re-reads the board after a
 * change; without them (the content page) the panel polls
 * `GET /api/contents/:id/posts` itself. Either way it reads the current
 * version, for the share copy and the files.
 */
export function PostsPanel({
  contentId,
  posts: given,
  content,
  onChanged,
  titled = true,
}: {
  readonly contentId: string;
  /** The posts as the caller already read them; left out, the panel polls them. */
  readonly posts?: readonly Post[];
  readonly content: PostsPanelContent;
  /** Something changed: the caller should read again. */
  readonly onChanged?: () => Promise<void>;
  /** Whether the panel shows its own heading; off inside a page panel that already names it. */
  readonly titled?: boolean;
}) {
  const toast = useToast();
  const [polled, setPolled] = useState<readonly Post[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [deleting, setDeleting] = useState<Post | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const polls = given === undefined;

  useEffect(() => {
    if (!polls) return undefined;
    const controller = new AbortController();
    const load = (): void => {
      fetchPosts(contentId, controller.signal)
        .then(setPolled)
        .catch(() => {
          // Keep the last reading, as every poll here does.
        });
    };
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, DASHBOARD_POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [contentId, polls]);

  const posts = given ?? polled;
  const versions = useVersions(contentId, content.currentVersion);
  const version =
    versions?.versions.find((candidate) => candidate.number === versions.currentVersion) ?? versions?.versions[0];
  const files = (posts ?? []).map((post) => fileFor(post, version));
  const urls = useAssetUrls(
    contentId,
    files.map((file) => file?.assetId ?? null),
  );
  const now = useNow(15_000);

  /** After a change: the panel's own reading (when it polls) and the caller's. */
  const settle = async (): Promise<void> => {
    if (polls) {
      try {
        setPolled(await fetchPosts(contentId));
      } catch {
        // The next poll catches up.
      }
    }
    await onChanged?.();
  };

  const missing =
    posts === null
      ? []
      : shareCopyPlatforms(content.shareCopy).filter(
          (platform) => !posts.some((post) => post.platform === platform),
        );

  const planPerPlatform = async (): Promise<void> => {
    setPlanning(true);
    let made = 0;
    try {
      for (const platform of missing) {
        await createPost(contentId, { platform, format: defaultPostFormat(platform, content.formats) });
        made += 1;
      }
      toast.ok(wording.postsPlanned(made, content.name));
    } catch (cause: unknown) {
      toast.error(describeError(cause));
    } finally {
      setPlanning(false);
      await settle();
    }
  };

  const remove = async (post: Post): Promise<void> => {
    setDeleteBusy(true);
    try {
      await deletePost(post.id);
      setDeleting(null);
      toast.ok(wording.postDeleted(postPlatformLabel(post.platform, post.label)));
      await settle();
    } catch (cause: unknown) {
      toast.error(describeError(cause));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <section className="board-drawer__section posts-panel">
      {titled && <h3 className="board-drawer__section-title">{wording.POSTS}</h3>}
      {posts === null ? (
        <p className="field__hint">{wording.POSTS_LOADING}</p>
      ) : posts.length === 0 ? (
        <p className="field__hint">{wording.POSTS_NONE}</p>
      ) : (
        <ul className="posts-panel__list">
          {posts.map((post, index) => (
            <PostRow
              key={post.id}
              post={post}
              content={content}
              version={version}
              file={files[index] ?? null}
              fileUrl={urlOf(files[index] ?? null, urls)}
              now={now}
              onChanged={settle}
              onDelete={() => {
                setDeleting(post);
              }}
            />
          ))}
        </ul>
      )}
      {content.status === 'rejected' ? (
        // The server refuses new posts of a rejected content (US-005).
        <p className="field__hint">{wording.POSTS_REJECTED}</p>
      ) : adding && posts !== null ? (
        <AddPostForm
          contentId={contentId}
          content={content}
          posts={posts}
          onClose={() => {
            setAdding(false);
          }}
          onChanged={settle}
        />
      ) : (
        <div className="board-drawer__actions">
          <button
            type="button"
            className="button"
            disabled={posts === null}
            onClick={() => {
              setAdding(true);
            }}
          >
            <Icon name="plus" />
            {wording.POST_ADD}
          </button>
          <button
            type="button"
            className="button"
            disabled={posts === null || missing.length === 0 || planning}
            title={posts !== null && missing.length === 0 ? wording.POST_PLAN_PER_PLATFORM_NONE : undefined}
            onClick={() => void planPerPlatform()}
          >
            {wording.POST_PLAN_PER_PLATFORM}
          </button>
        </div>
      )}
      <ConfirmDialog
        open={deleting !== null}
        title={wording.POST_DELETE_TITLE}
        confirmLabel={wording.POST_DELETE}
        cancelLabel={wording.CANCEL}
        busy={deleteBusy}
        danger
        onConfirm={() => {
          if (deleting !== null) void remove(deleting);
        }}
        onCancel={() => {
          setDeleting(null);
        }}
      >
        <p>
          {deleting === null
            ? ''
            : wording.postDeleteBody(postPlatformLabel(deleting.platform, deleting.label), content.name)}
        </p>
      </ConfirmDialog>
    </section>
  );
}

/** The version file a post goes out with: the one rendered at its format, else the render. */
function fileFor(post: Post, version: Version | undefined): VersionFile | null {
  if (version === undefined) return null;
  const candidates = [version.render, ...version.variants].filter((file): file is VersionFile => file !== null);
  return candidates.find((file) => post.format !== null && file.preset === post.format) ?? version.render;
}

function urlOf(file: VersionFile | null, urls: ReadonlyMap<string, { readonly url: string }>): string | null {
  return file === null || file.assetId === null ? null : (urls.get(file.assetId)?.url ?? null);
}

/** The post's own caption, else the share copy block for its platform, else the first block. */
function captionFor(post: Post, version: Version | undefined): string | null {
  if (post.caption !== null && post.caption !== '') return post.caption;
  const blocks: readonly SharePost[] = version?.shareCopy?.posts ?? [];
  const block = blocks.find((candidate) => candidate.platform === post.platform) ?? blocks[0];
  return block === undefined || block.body.trim() === '' ? null : block.body;
}

type RowMode = 'idle' | 'posting' | 'editing' | 'notes';

/** One post: what and when, the buttons that go with its status, and the inline form that is open. */
function PostRow({
  post,
  content,
  version,
  file,
  fileUrl,
  now,
  onChanged,
  onDelete,
}: {
  readonly post: Post;
  readonly content: PostsPanelContent;
  readonly version: Version | undefined;
  readonly file: VersionFile | null;
  readonly fileUrl: string | null;
  readonly now: number;
  readonly onChanged: () => Promise<void>;
  readonly onDelete: () => void;
}) {
  const toast = useToast();
  const id = useId();
  const [mode, setMode] = useState<RowMode>('idle');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [draft, setDraft] = useState<PostDraft>(() => draftOfPost(post));

  const platform = postPlatformLabel(post.platform);
  const named = postPlatformLabel(post.platform, post.label);
  const planned = post.status === 'planned';
  const due = planned && post.scheduledAt !== null && Date.parse(post.scheduledAt) <= now;
  const caption = captionFor(post, version);
  const composer = composerFor(post.platform, post.paid);
  const delivered = content.status === 'delivered';
  // A rejected content's planned post can be deleted, but not posted or changed (US-005).
  const rejected = content.status === 'rejected';

  const open = (next: RowMode): void => {
    setError(null);
    if (next === 'posting') setUrl('');
    if (next === 'editing') setDraft(draftOfPost(post));
    if (next === 'notes') setNotes(post.notes ?? '');
    setMode(next);
  };

  /** Runs one change; a refusal stays beside the open form, which stays open. */
  const save = async (work: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await work();
      setMode('idle');
      await onChanged();
    } catch (cause: unknown) {
      if (mode === 'idle') toast.error(describeError(cause));
      else setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  const undo = (): Promise<void> =>
    save(async () => {
      await undoPostPosted(post.id);
      toast.ok(wording.postUnposted(content.name, named));
    });

  const markPosted = (event: FormEvent): void => {
    event.preventDefault();
    const link = url.trim();
    void save(async () => {
      await markPostPosted(post.id, { url: link === '' ? null : link });
      toast.ok(wording.postedOnPlatform(content.name, named), {
        label: wording.UNDO,
        onClick: () => {
          undoPostPosted(post.id)
            .then(onChanged)
            .catch((cause: unknown) => {
              toast.error(describeError(cause));
            });
        },
      });
    });
  };

  const copyCaption = (): void => {
    if (caption === null) return;
    navigator.clipboard
      .writeText(caption)
      .then(() => {
        toast.ok(wording.copied(wording.POST_CAPTION));
      })
      .catch(() => {
        toast.error(wording.COPY_BLOCKED);
      });
  };

  let when;
  if (!planned) {
    const moment = wording.postedOn(post.postedAt === null ? '—' : postMoment(post.postedAt));
    when =
      post.url === null ? (
        moment
      ) : (
        <a className="link" href={post.url} target="_blank" rel="noopener">
          {moment}
        </a>
      );
  } else if (post.scheduledAt === null) {
    when = <span className="muted">{wording.POST_NO_DATE}</span>;
  } else {
    when = postMoment(post.scheduledAt);
  }

  let download;
  if (file === null || file.assetId === null || fileUrl === null) {
    let reason = wording.POST_NO_RENDER;
    if (file !== null) reason = file.assetId === null ? wording.POST_NOT_IN_BUCKET : wording.DOWNLOAD_WAITING;
    download = (
      <button type="button" className="button button--small" disabled title={reason}>
        <Icon name="file" />
        {wording.DOWNLOAD}
      </button>
    );
  } else {
    download = (
      <a className="button button--small" href={fileUrl} download={file.name} title={file.name}>
        <Icon name="file" />
        {wording.DOWNLOAD}
      </a>
    );
  }

  return (
    <li className={`posts-panel__post${planned ? '' : ' posts-panel__post--posted'}`}>
      <p className="posts-panel__what">
        <strong>{platform}</strong>
        {post.paid && <Badge tone="wait">{wording.POST_AD}</Badge>}
        {post.label !== null && <span>{post.label}</span>}
        {post.format !== null && <span className="muted">{post.format}</span>}
      </p>
      <p className="posts-panel__when">
        {when}
        {due && <Badge tone="danger">{wording.POST_DUE}</Badge>}
      </p>
      {post.notes !== null && mode !== 'notes' && <p className="posts-panel__notes muted">{post.notes}</p>}

      {mode === 'idle' && planned && (
        <div className="posts-panel__actions">
          <button
            type="button"
            className="button button--small"
            disabled={caption === null}
            title={caption === null ? wording.POST_NO_SHARE_COPY : undefined}
            onClick={copyCaption}
          >
            <Icon name="copy" />
            {wording.POST_COPY_CAPTION}
          </button>
          {download}
          {!rejected && composer !== null && (
            <a className="button button--small" href={composer.url} target="_blank" rel="noopener">
              <Icon name="link-external" />
              {wording.openComposer(composer.label)}
            </a>
          )}
          {!rejected && (
            <>
              <button
                type="button"
                className="button button--small"
                disabled={!delivered}
                title={delivered ? undefined : wording.POST_APPROVE_FIRST}
                onClick={() => {
                  open('posting');
                }}
              >
                <Icon name="check" />
                {wording.POST_MARK_POSTED}
              </button>
              <button
                type="button"
                className="button button--small button--quiet"
                onClick={() => {
                  open('editing');
                }}
              >
                {wording.POST_EDIT}
              </button>
            </>
          )}
          <button type="button" className="button button--small button--quiet button--danger" onClick={onDelete}>
            {wording.POST_DELETE}
          </button>
        </div>
      )}

      {mode === 'idle' && !planned && (
        <div className="posts-panel__actions">
          {post.url !== null && (
            <a className="button button--small" href={post.url} target="_blank" rel="noopener">
              <Icon name="link-external" />
              {wording.POST_OPEN}
            </a>
          )}
          <button type="button" className="button button--small" disabled={busy} onClick={() => void undo()}>
            {wording.UNDO}
          </button>
          <button
            type="button"
            className="button button--small button--quiet"
            onClick={() => {
              open('notes');
            }}
          >
            {wording.POST_EDIT_NOTES}
          </button>
        </div>
      )}

      {mode === 'posting' && (
        <form className="posts-panel__form" onSubmit={markPosted}>
          <label className="field__label" htmlFor={`${id}-url`}>
            {wording.POST_URL}
          </label>
          <div className="posts-panel__inline">
            <input
              id={`${id}-url`}
              className="field__input"
              type="url"
              placeholder="https://"
              maxLength={500}
              autoFocus
              value={url}
              onChange={(event) => {
                setUrl(event.target.value);
              }}
            />
            <button type="submit" className="button button--small button--primary" disabled={busy}>
              {busy ? wording.POST_SAVING : wording.SAVE}
            </button>
            <CancelButton onClick={() => setMode('idle')} />
          </div>
          <FormError error={error} />
        </form>
      )}

      {mode === 'editing' && (
        <form
          className="posts-panel__form"
          onSubmit={(event) => {
            event.preventDefault();
            void save(async () => {
              await updatePost(post.id, postInputOf(draft, true));
            });
          }}
        >
          <PostFields draft={draft} formats={content.formats} withCaption onChange={setDraft} />
          <FormError error={error} />
          <div className="board-drawer__actions">
            <button
              type="submit"
              className="button button--small button--primary"
              disabled={busy || draftLabelMissing(draft)}
            >
              {busy ? wording.POST_SAVING : wording.SAVE}
            </button>
            <CancelButton onClick={() => setMode('idle')} />
          </div>
        </form>
      )}

      {mode === 'notes' && (
        <form
          className="posts-panel__form"
          onSubmit={(event) => {
            event.preventDefault();
            const text = notes.trim();
            void save(async () => {
              await updatePost(post.id, { notes: text === '' ? null : text });
            });
          }}
        >
          <label className="field__label" htmlFor={`${id}-notes`}>
            {wording.POST_NOTES}
          </label>
          <textarea
            id={`${id}-notes`}
            className="field__input field__textarea"
            rows={3}
            maxLength={1000}
            autoFocus
            value={notes}
            onChange={(event) => {
              setNotes(event.target.value);
            }}
          />
          <FormError error={error} />
          <div className="board-drawer__actions">
            <button type="submit" className="button button--small button--primary" disabled={busy}>
              {busy ? wording.POST_SAVING : wording.SAVE}
            </button>
            <CancelButton onClick={() => setMode('idle')} />
          </div>
        </form>
      )}
    </li>
  );
}

/** **Add post**: ScheduleDialog's form, inline (US-024). */
function AddPostForm({
  contentId,
  content,
  posts,
  onClose,
  onChanged,
}: {
  readonly contentId: string;
  readonly content: PostsPanelContent;
  readonly posts: readonly Post[];
  readonly onClose: () => void;
  readonly onChanged: () => Promise<void>;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState<PostDraft>(() => newPostDraft(content, posts));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const input = postInputOf(draft);
    try {
      await createPost(contentId, input);
    } catch (cause: unknown) {
      // 400 `invalid_post` names the field; the form stays as typed.
      setError(describeError(cause));
      setBusy(false);
      return;
    }
    const where = postPlatformLabel(draft.platform, input.label ?? null);
    toast.ok(
      input.scheduledAt === null || input.scheduledAt === undefined
        ? wording.postsPlanned(1, content.name)
        : wording.scheduled(content.name, where),
    );
    setBusy(false);
    onClose();
    await onChanged();
  };

  return (
    <form
      className="posts-panel__form posts-panel__add"
      onSubmit={(event) => {
        event.preventDefault();
        void add();
      }}
    >
      <PostFields draft={draft} formats={content.formats} onChange={setDraft} />
      <FormError error={error} />
      <div className="board-drawer__actions">
        <button type="submit" className="button button--primary" disabled={busy || draftLabelMissing(draft)}>
          {busy ? wording.POST_ADD_BUSY : wording.POST_ADD}
        </button>
        <CancelButton onClick={onClose} />
      </div>
    </form>
  );
}

function CancelButton({ onClick }: { readonly onClick: () => void }) {
  return (
    <button type="button" className="button button--small button--quiet" onClick={onClick}>
      {wording.CANCEL}
    </button>
  );
}

function FormError({ error }: { readonly error: string | null }) {
  if (error === null) return null;
  return (
    <p className="field__hint field__hint--error" role="alert">
      {error}
    </p>
  );
}
